import type { ClassSummary } from '../../../shared/api/classes'
import type { StaffIdentity } from '../../../shared/api/auth'

/**
 * The name to greet the signed-in teacher by.
 *
 * 🔴 IT EXISTS BECAUSE THE TIER USED TO GREET PEOPLE BY SOMEBODY ELSE'S NAME.
 * `TeacherApp` had `teacherName ?? openClass?.teacher?.name ?? ''`, the router
 * never passed the prop, and so the fallback is what rendered: the name of
 * whoever RUNS the class on screen. That was invisible for as long as a teacher
 * could only open a class they ran — the wrong name was always their own. E4's
 * subject teachers open classes they do not run, and the greeting began saying
 * "Hello, Nishant" to a colleague.
 *
 * ⚠️ THE CLASS IS NOT AN ARGUMENT HERE, AND THAT IS THE POINT. There is no
 * fourth fallback to add: if the account's own name cannot be had, the honest
 * answer is none, and `greeting('')` is "Hello".
 *
 * ⚠️ THE EMAIL IS THE SECOND CHOICE, because a staff row may carry no name and a
 * work address still identifies the person reading it — `SchoolStaffRow`'s rule
 * for the admin's table, applied to the person's own screen.
 */
/**
 * May this person TAKE the register for this class — not merely read it?
 *
 * 🔴 THE ONE THING ON THIS TIER A SUBJECT TEACHER MUST NOT DO, AND THE SERVER
 * HAS ALWAYS SAID SO. `AttendanceService.writableClass` is the only write in the
 * product that asks `class.teacherId === principal.userId` directly rather than
 * going through `ScopeService` — deliberately, because taking a register
 * MESSAGES FAMILIES. A class has one teacher of record; the person a parent
 * hears from about their child's absence has to be that one person, not
 * whichever of six subject teachers opened the screen first.
 *
 * ⚠️ SO THIS FUNCTION CHANGES NOTHING ABOUT WHAT IS ALLOWED. The refusal is the
 * server's and stays the server's. This decides what is DRAWN — D4's rule: *"a
 * control that cannot work is worse than no control"*. Without it a subject
 * teacher marks a class absent, presses Save, and is told no by a 403 after
 * doing the work.
 *
 * 🔴 AN UNKNOWN IDENTITY MEANS YES, AND THAT IS THE DELIBERATE DIRECTION.
 * `GET /auth/me` is a small call beside a register; if it fails, the person who
 * loses their morning must not be the class teacher whose job this screen is.
 * Guessing "yes" restores exactly the behaviour that shipped before subject
 * teachers existed, and the server still refuses anybody it should — guessing
 * "no" would take the register away from the one person entitled to it, for a
 * reason they cannot see or fix.
 */
export function mayTakeRegister(
  identity: StaffIdentity | null | undefined,
  klass: ClassSummary | null | undefined,
): boolean {
  if (!identity) return true
  if (!klass) return false
  return klass.teacher !== null && klass.teacher.id === identity.userId
}

export function signedInName(
  identity: StaffIdentity | null | undefined,
  override?: string,
): string {
  /* An explicitly-passed name wins — a caller that knows is not second-guessed.
     Blank is treated as absent so a `''` prop cannot suppress the real name. */
  if (override !== undefined && override.trim() !== '') return override
  if (!identity) return ''
  if (identity.name !== null && identity.name.trim() !== '') return identity.name
  return identity.email ?? ''
}

/**
 * Which class the teacher tier opens on.
 *
 * 🔴 THE WHOLE TIER WAS BLOCKED ON THIS ONE DECISION, AND IT WAS LEFT AS AN
 * EMPTY STRING. `AppRouter` held `const [classId] = useState('')` with the
 * reason written next to it — *"a teacher with two classes has no default, and
 * choosing one for them would show the wrong roster to half of them"* — which is
 * correct, and stopped one class short of the answer. The honest rule is not
 * "never choose"; it is **choose only when there is nothing to choose between.**
 */

export type ClassChoice =
  /** Nothing to pick from. The teacher has no live class assigned. */
  | { kind: 'NONE' }
  /**
   * Exactly one. Selected without asking — a picker with a single option is a
   * tap that cannot go wrong and therefore should not be offered, and it costs
   * a teacher two seconds of a thirty-minute slot every morning.
   */
  | { kind: 'ONLY'; classId: string }
  /**
   * 🔴 MORE THAN ONE, AND NOTHING IS PRE-SELECTED. This is the case the original
   * comment was protecting: a default here shows one class's roster under
   * another class's name, and the teacher finds out when a session lands on the
   * wrong child. They pick; we do not guess.
   */
  | { kind: 'MANY'; options: ClassSummary[] }

/**
 * ⚠️ ARCHIVED CLASSES ARE FILTERED HERE **AS WELL AS** ON THE SERVER, and the
 * belt is worth the braces.
 *
 * `getClasses` does not send `?includeArchived=true`, so the server already
 * excludes them. This repeats it because an archived class is last year's
 * cohort (D3: *"move the leavers into a Class of 20XX and archive it"*) — a
 * roster a teacher could tap into and start a real session against, attributing
 * a 2027 session to a child who left in 2026. A filter applied in one place is
 * a filter someone eventually adds a caller around.
 */
export function chooseClass(classes: readonly ClassSummary[]): ClassChoice {
  const live = classes.filter((c) => c.archivedAt === null)

  if (live.length === 0) return { kind: 'NONE' }
  if (live.length === 1) return { kind: 'ONLY', classId: live[0].id }

  /*
   * Ordered by name so the list does not reshuffle between refreshes. A teacher
   * reaching for "the second one" must find the same class there each morning —
   * `useResource` is deliberately not a cache, so every visit is a fresh fetch
   * and the server's ordering is not something this screen may assume.
   */
  return { kind: 'MANY', options: [...live].sort((a, b) => a.name.localeCompare(b.name)) }
}

/**
 * The class currently open, resolved against what the teacher may actually see.
 *
 * 🔴 A REMEMBERED ID IS RE-VALIDATED, NEVER TRUSTED. The selection lives in
 * component state across a re-fetch, and a class can disappear between the two
 * — archived by an admin mid-morning, or reassigned to another teacher (§8.3
 * deactivation moves scope). Rendering a screen keyed on an id the server no
 * longer returns produces a 403 on every panel with no explanation on screen.
 * Falling back to "nothing selected" puts the picker back, which is a state the
 * teacher can act on.
 */
export function resolveSelected(
  classes: readonly ClassSummary[],
  selected: string | null,
): string | null {
  const choice = chooseClass(classes)
  if (choice.kind === 'NONE') return null
  if (choice.kind === 'ONLY') return choice.classId
  if (selected === null) return null
  return choice.options.some((c) => c.id === selected) ? selected : null
}

/** The open class's own row, for the name D1 greets the teacher with. */
export function findClass(
  classes: readonly ClassSummary[],
  classId: string | null,
): ClassSummary | null {
  if (classId === null) return null
  return classes.find((c) => c.id === classId) ?? null
}

/* ─────────────────────────────────────────────── grade and section ── */

/**
 * How a class is written on a timetable: **"Class 2 · B"**.
 *
 * 🔴 IT FALLS BACK TO `name`, AND NEVER RENDERS A DANGLING SEPARATOR. Both
 * columns are nullable — every row created before the migration has both null,
 * and a single-stream nursery legitimately has no section. A naive
 * `${grade} · ${section}` produces " · B" or "Class 2 · " on those rows, which
 * reads as data that failed to load.
 */
export function classLabel(c: ClassSummary): string {
  const parts = [c.grade, c.section].filter((p): p is string => p !== null && p.trim() !== '')
  return parts.length > 0 ? parts.join(' · ') : c.name
}

export interface GradeGroup {
  /** `null` groups every class that has no grade set. */
  grade: string | null
  classes: ClassSummary[]
}

/**
 * The picker, grouped the way a teacher thinks about it.
 *
 * 🔴 A TEACHER WITH SIX CLASSES HAS THEM IN TWO OR THREE GRADES, and scanning a
 * flat list of six similar names is where the wrong section gets tapped — which
 * costs a whole session attributed to the wrong roster. Grouping puts "Class 2"
 * above its sections, so the choice is made in two small steps instead of one
 * six-way one.
 *
 * ⚠️ UNGRADED CLASSES SORT LAST AND ARE NOT HIDDEN. They are the pre-migration
 * rows and the schools that do not stream; dropping them would make a teacher's
 * only class invisible on the very upgrade that added the feature.
 */
export function groupByGrade(classes: readonly ClassSummary[]): GradeGroup[] {
  const groups = new Map<string, ClassSummary[]>()
  const UNGRADED = ' '

  for (const c of classes) {
    const key = c.grade === null || c.grade.trim() === '' ? UNGRADED : c.grade
    const list = groups.get(key)
    if (list) list.push(c)
    else groups.set(key, [c])
  }

  return [...groups.entries()]
    .sort(([a], [b]) => {
      // The sentinel sorts last, whatever the other grade is called.
      if (a === UNGRADED) return 1
      if (b === UNGRADED) return -1
      /*
       * `numeric` so "Class 2" precedes "Class 10" — a plain string compare puts
       * 10 before 2, and a school with ten grades reads it as a bug every time.
       */
      return a.localeCompare(b, undefined, { numeric: true })
    })
    .map(([key, list]) => ({
      grade: key === UNGRADED ? null : key,
      classes: [...list].sort((a, b) =>
        (a.section ?? a.name).localeCompare(b.section ?? b.name, undefined, { numeric: true }),
      ),
    }))
}
